import React, { useState } from "react";
import { createData } from "../../api/API";
import ToastMessage from "./ToastMessage";
import { useInput } from "../hook/useInput";
import FloatingLabelInput from "../floatingLabelInput/FloatingLabelInput";
import { NavLink, useNavigate } from "react-router-dom";
import { Col, Form, Button, Row } from "react-bootstrap";
import { FaHandshake, FaUserPlus, FaLock, FaUser } from "react-icons/fa";
import "../styles/Login.css";

const SignUpForm = () => {
  const navigate = useNavigate();

  const [user_name, bindUserName] = useInput("");
  const [user_email, bindUserEmail] = useInput("");
  const [user_phonenumber, setUserPhoneNumber] = useState("");
  const [user_password, bindUserPassword] = useInput("");
  const [confirmPassword, bindConfirmPassword] = useInput("");
  const [company_name, bindCompanyName] = useInput("");
  const [formErrors, setFormErrors] = useState({});
  const [showToast, setShowToast] = useState({
    flag: false,
    message: "",
    bg: "",
  });

  const validateEmail = (email) => {
    return /^\S+@\S+\.\S+$/.test(email) ? "" : "Invalid email address";
  };

  const validatePassword = (password) => {
    return /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/.test(
      password
    )
      ? ""
      : "Password should be at least 8 characters, include 1 letter, 1 number, and 1 special character";
  };

  const validatePhoneNumber = (phoneNumber) => {
    return /^[0-9]{10}$/.test(phoneNumber)
      ? ""
      : "Enter a valid 10-digit phone number";
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    return confirmPassword === password ? "" : "Passwords don't match";
  };

  const handlePhoneNumberChange = (e) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, "");
    const truncatedValue = numericValue.slice(0, 10);
    setUserPhoneNumber(truncatedValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormErrors({});
    setShowToast({ flag: false, message: "", bg: "" });

    let errors = {};
    errors.user_email = validateEmail(user_email);
    errors.user_password = validatePassword(user_password);
    errors.user_phonenumber = validatePhoneNumber(user_phonenumber);
    errors.confirmPassword = validateConfirmPassword(
      confirmPassword,
      user_password
    );

    if (Object.values(errors).some((error) => error !== "")) {
      setFormErrors(errors);
      return;
    }

    try {
      if (user_password !== confirmPassword) {
        setFormErrors({ confirmPassword: "Passwords don't match!" });
        return;
      }
      
      const formattedUser = {
        user_name,
        user_email,
        user_phonenumber: user_phonenumber.replace(/[^0-9]/g, ""),
        user_password,
        company_name,
      };

      const res = await createData("users", formattedUser);

      if (res && res.data?.status === "success") {
        setShowToast({
          flag: true,
          message: "Signup successful! Redirecting to login...",
          bg: "success",
        });
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else if (res && res.data?.error) {
        setShowToast({
          flag: true,
          message: "Something went wrong! " + (res.data.error.message || ""),
          bg: "danger",
        });
        setFormErrors(res.data.error);
      }
    } catch (error) {
      console.error("Error during signup:", error);
      setShowToast({
        flag: true,
        message: "Something went wrong! Please try again.",
        bg: "danger",
      });
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center w-100 min-vh-100 p-3 bg-light">
      <ToastMessage showToast={showToast} setShowToast={setShowToast} />
      <div className="erp-auth-wrapper" style={{ maxWidth: "1050px" }}>
        <Row className="g-0">
          <Col md={5}>
            <div className="erp-auth-hero">
              <div>
                <div className="d-flex align-items-center gap-2 mb-4">
                  <img
                    src="/images/Logo1.png"
                    alt="Soochi Logo"
                    style={{ height: "38px", width: "auto", objectFit: "contain" }}
                  />
                  <span className="fs-4 fw-bold text-white tracking-tight">
                    SOOCHI 
                  </span>
                </div>
                <h2 className="erp-auth-hero-title">
                  Begin Your Business Transformation
                </h2>
                <p className="erp-auth-hero-desc">
                  Join hundreds of businesses managing multi-channel billing, automated inventory, and enterprise growth.
                </p>
              </div>

              <div className="d-flex align-items-center gap-3 pt-4 border-top border-white-50">
                <div className="p-2 rounded-3 bg-white text-primary">
                  <FaHandshake size={20} />
                </div>
                <div>
                  <div className="fw-semibold text-white small">Instant Setup</div>
                  <div className="text-white-50 small">Configure company workspace in under 2 minutes</div>
                </div>
              </div>
            </div>
          </Col>

          <Col md={7}>
            <div className="erp-auth-form-container">
              <h3 className="erp-auth-title">Create Workspace Account</h3>
              <p className="erp-auth-subtitle">
                Enter your business details below to register your account.
              </p>

              <Form onSubmit={handleSubmit}>
                <Row className="g-2">
                  <Col md={6} xs={12}>
                    <FloatingLabelInput
                      id="user_name"
                      {...bindUserName}
                      type="text"
                      label="Full Name"
                      errorMessage={formErrors.user_name}
                      required
                    />
                  </Col>
                  <Col md={6} xs={12}>
                    <FloatingLabelInput
                      id="user_email"
                      {...bindUserEmail}
                      type="email"
                      label="Email Address"
                      errorMessage={formErrors.user_email}
                      required
                    />
                  </Col>
                  <Col md={6} xs={12}>
                    <FloatingLabelInput
                      id="user_phonenumber"
                      type="text"
                      label="Contact Number"
                      errorMessage={formErrors.user_phonenumber}
                      pattern="^[0-9]{10}$"
                      maxLength="10"
                      required
                      value={user_phonenumber}
                      onChange={handlePhoneNumberChange}
                    />
                  </Col>
                  <Col md={6} xs={12}>
                    <FloatingLabelInput
                      id="company_name"
                      {...bindCompanyName}
                      type="text"
                      label="Company / Enterprise Name"
                      errorMessage={formErrors.company_name}
                      required
                    />
                  </Col>
                  <Col md={6} xs={12}>
                    <FloatingLabelInput
                      id="user_password"
                      {...bindUserPassword}
                      type="password"
                      label="Password"
                      errorMessage={formErrors.user_password}
                      required
                    />
                  </Col>
                  <Col md={6} xs={12}>
                    <FloatingLabelInput
                      id="confirmPassword"
                      {...bindConfirmPassword}
                      type="password"
                      label="Confirm Password"
                      errorMessage={formErrors.confirmPassword}
                      pattern={`^${user_password}$`}
                      required
                    />
                  </Col>
                </Row>

                {formErrors.message && (
                  <div className="text-danger small mt-2">{formErrors.message}</div>
                )}

                <Button
                  type="submit"
                  className="erp-auth-btn d-flex align-items-center justify-content-center gap-2 mt-3"
                >
                  <FaUserPlus size={14} /> Register Organization Account
                </Button>
              </Form>

              <div className="mt-4 text-center text-muted small">
                Already have an account?{" "}
                <NavLink to="/login" className="fw-bold text-primary text-decoration-none">
                  Sign In
                </NavLink>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SignUpForm;
